import { useEffect } from "react";
import { Link } from "react-router-dom";
import { WasteIcon, ImportIcon, HazardIcon, BatteryIcon, CycleIcon, ChartIcon } from "../Icons";

function WhyItMattersPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    const reveals = document.querySelectorAll(".reveal");
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add("visible"); observer.unobserve(e.target); }
      });
    }, { threshold: 0.1 });
    reveals.forEach((r) => observer.observe(r));
    return () => observer.disconnect();
  }, []);

  return (
    <main style={{ paddingTop: "80px" }}>

      {/* PAGE HERO */}
      <section className="page-hero">
        <div className="hero-grid-bg"></div>
        <div className="hero-glow" style={{ opacity: 0.4, background: "radial-gradient(circle, rgba(245,166,35,.12) 0%, transparent 70%)" }}></div>
        <div className="section-inner" style={{ position: "relative", zIndex: 2 }}>
          <div className="section-tag">Why This Matters</div>
          <h1 className="section-title display" style={{ fontSize: "clamp(3rem, 6vw, 5.5rem)", marginBottom: "1.2rem" }}>
            India Has a Battery<br />Waste Crisis Coming
          </h1>
          <p className="section-body" style={{ maxWidth: "620px" }}>
            The EV revolution is the right call for India's climate future. But nobody is talking loudly enough about what happens to the batteries when they die. We are.
          </p>
        </div>
      </section>

      {/* THREE CORE PROBLEMS */}
      <section>
        <div className="section-inner">
          <div className="section-tag">The Problem</div>
          <h2 className="section-title display">Three Converging Crises</h2>

          <div className="page-problem-deep reveal">

            <div className="ppd-card">
              <div className="ppd-header">
                <div className="ppd-icon"><WasteIcon size={36} /></div>
                <div>
                  <div className="problem-num" style={{ fontSize: "2.5rem" }}>10X</div>
                  <h3>Exponential Waste Growth</h3>
                </div>
              </div>
              <p>
                India sold over 1.5 million EVs in FY2023 alone. With battery lifespans of 5–8 years, this translates to millions of batteries entering end-of-life within this decade. Current recycling infrastructure handles a fraction of projected volumes — the gap is widening every quarter.
              </p>
              <div className="ppd-stat-row">
                <div className="ppd-stat">
                  <span className="ppd-stat-num">1.5M+</span>
                  <span className="ppd-stat-label">EVs sold in FY23</span>
                </div>
                <div className="ppd-stat">
                  <span className="ppd-stat-num">5–8yr</span>
                  <span className="ppd-stat-label">Avg battery lifespan</span>
                </div>
                <div className="ppd-stat">
                  <span className="ppd-stat-num">&lt;5%</span>
                  <span className="ppd-stat-label">Currently recycled</span>
                </div>
              </div>
            </div>

            <div className="ppd-card">
              <div className="ppd-header">
                <div className="ppd-icon"><ImportIcon size={36} /></div>
                <div>
                  <div className="problem-num" style={{ fontSize: "2.5rem" }}>90%+</div>
                  <h3>Critical Mineral Dependency</h3>
                </div>
              </div>
              <p>
                India imports nearly all of the lithium, cobalt, and nickel it needs to manufacture batteries — from geopolitically sensitive regions like the DRC, Chile, and China. Every tonne of unrecycled battery waste is a missed opportunity to build domestic mineral supply chains. Without a recycling ecosystem, India's EV ambitions are built on foreign supply chains it cannot control.
              </p>
              <div className="ppd-stat-row">
                <div className="ppd-stat">
                  <span className="ppd-stat-num">DRC</span>
                  <span className="ppd-stat-label">Cobalt source (70%+)</span>
                </div>
                <div className="ppd-stat">
                  <span className="ppd-stat-num">Chile</span>
                  <span className="ppd-stat-label">Lithium brine source</span>
                </div>
                <div className="ppd-stat">
                  <span className="ppd-stat-num">China</span>
                  <span className="ppd-stat-label">Refined materials</span>
                </div>
              </div>
            </div>

            <div className="ppd-card" style={{ borderColor: "rgba(245,166,35,0.3)" }}>
              <div className="ppd-header">
                <div className="ppd-icon"><HazardIcon size={36} /></div>
                <div>
                  <div className="problem-num" style={{ fontSize: "2.5rem", color: "var(--amber)" }}>⚠</div>
                  <h3>Improper Disposal Risk</h3>
                </div>
              </div>
              <p>
                Unprocessed lithium-ion batteries in landfills or informal scrap yards pose serious environmental and public safety risks. Thermal runaway events, heavy metal leaching into groundwater, and toxic electrolyte exposure are real, documented hazards. India's Battery Waste Management Rules 2022 are already in force — non-compliant producers face escalating penalties.
              </p>
              <div className="ppd-stat-row">
                <div className="ppd-stat">
                  <span className="ppd-stat-num" style={{ color: "var(--amber)" }}>Fire</span>
                  <span className="ppd-stat-label">Thermal runaway risk</span>
                </div>
                <div className="ppd-stat">
                  <span className="ppd-stat-num" style={{ color: "var(--amber)" }}>Toxic</span>
                  <span className="ppd-stat-label">Soil &amp; groundwater</span>
                </div>
                <div className="ppd-stat">
                  <span className="ppd-stat-num" style={{ color: "var(--amber)" }}>EPR</span>
                  <span className="ppd-stat-label">Mandatory since 2022</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* REGULATORY CONTEXT */}
      <section style={{ background: "var(--panel)" }}>
        <div className="section-inner">
          <div className="section-tag">Regulatory Backdrop</div>
          <h2 className="section-title display">The Policy Clock Is Ticking</h2>
          <p className="section-body">
            India's government is acting. Here's what producers need to know.
          </p>

          <div className="timeline reveal">
            {[
              { year: "2022", event: "Battery Waste Management Rules notified", detail: "EPR framework made mandatory for battery producers, importers, and brand owners." },
              { year: "2023", event: "EPR targets go live", detail: "Phase-in targets for collection and recycling begin. Non-compliance triggers financial penalties." },
              { year: "2025", event: "Targets scale up significantly", detail: "Producers required to account for a much larger proportion of batteries sold. Enforcement tightens." },
              { year: "2027+", event: "Full circular economy mandate", detail: "100% EPR compliance expected across all battery categories. No approved recycler partner = regulatory liability." },
            ].map((t) => (
              <div className="timeline-item" key={t.year}>
                <div className="timeline-year">{t.year}</div>
                <div className="timeline-content">
                  <h4>{t.event}</h4>
                  <p>{t.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OUR ANSWER */}
      <section>
        <div className="section-inner">
          <div className="section-tag">Our Response</div>
          <h2 className="section-title display">This Is Why We Exist</h2>
          <p className="section-body">
            Alankaar Power was built specifically to address these three converging problems — waste volume, mineral dependency, and regulatory risk — with a single, integrated solution.
          </p>
          <div className="compliance-row reveal" style={{ marginTop: "3rem" }}>
            <div className="compliance-item">
              <div className="usp-icon-wrap" style={{ marginBottom: "1rem" }}><BatteryIcon size={22} /></div>
              <h3>We Absorb the Volume</h3>
              <p>Scalable from 5 TPD today to 50+ TPD — designed to grow as India's waste stream grows.</p>
            </div>
            <div className="compliance-item">
              <div className="usp-icon-wrap" style={{ marginBottom: "1rem" }}><CycleIcon size={22} /></div>
              <h3>We Close the Loop</h3>
              <p>Recovered minerals re-enter the domestic supply chain, reducing import dependency tonne by tonne.</p>
            </div>
            <div className="compliance-item">
              <div className="usp-icon-wrap" style={{ marginBottom: "1rem" }}><ChartIcon size={22} /></div>
              <h3>We Handle Compliance</h3>
              <p>EPR certificates issued to producer partners. Your regulatory obligation, solved.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: "var(--panel)", borderTop: "var(--accent-line)" }}>
        <div className="section-inner" style={{ textAlign: "center" }}>
          <h2 className="section-title display">Be Part of the Solution</h2>
          <p className="section-body" style={{ margin: "0 auto 2rem" }}>
            Every battery recycled is a step toward India's energy independence. Partner with us.
          </p>
          <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
            <a href="/#contact" className="btn-primary">Get in Touch</a>
            <Link to="/" className="btn-outline">← Back to Home</Link>
          </div>
        </div>
      </section>

    </main>
  );
}

export default WhyItMattersPage;